import dayjs from "dayjs";
import { X } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { formatNumber } from "@/utils/date.time.utils";
import {
	ShiftPlannerPendingEntry,
	ShiftPlannerPendingShiftRef,
	ShiftPlannerPendingStatus,
	ShiftPlannerShift,
} from "./shiftPlanner.types";

export type ShiftPillVariant = "booked" | "unassigned" | "invited" | "absence";

export interface ShiftPillProps {
	shift: ShiftPlannerShift;
	variant: ShiftPillVariant;
	/** The pill represents this many identical shifts ("10-14 ×3") — demand
	 * grouping; 1 renders a plain pill. */
	count?: number;
	/** Makes the count capsule a button — fan the group out into individual
	 * pills (see the planner's expandedGroupCells view state). */
	onCountClick?: () => void;
	/** Tighter paddings + letter spacing so times and the ✕ fit the narrower
	 * cells of the planner's narrow mode. */
	narrow?: boolean;
	/** Outline the pill — the consultant's entry in a pending card is hovered
	 * and this shift is one of theirs: solid outline when they applied to it,
	 * dashed when they are invited to it (matching the badge segments). */
	highlighted?: ShiftPlannerPendingStatus | false;
	/** Lazily resolves the pending card's entries (called on open only —
	 * it walks the whole job's shifts). */
	getPendingDetails?: (shift: ShiftPlannerShift) => ShiftPlannerPendingEntry[];
	/** Highlight these shifts in the grid ([] clears) — fired while a
	 * consultant entry in the pending card is hovered. */
	onHighlightShifts?: (shifts: ShiftPlannerPendingShiftRef[]) => void;
	/** Whether THIS pill's pending card is open — the planner owns the state
	 * so only one card can be open across the whole grid. */
	cardOpen?: boolean;
	onCardOpenChange?: (open: boolean) => void;
	/** "Boka" on an applied entry in the pending card — accept the application. */
	onAcceptEntry?: (entry: ShiftPlannerPendingEntry) => void;
	/** "Ta bort önskan" on an invited entry in the pending card. */
	onUninviteEntry?: (entry: ShiftPlannerPendingEntry) => void;
	onClick?: () => void;
	/** Double-click — the planner's shortcut straight to the consultant picker
	 * on open shifts. Fired on the SECOND PRESS (mousedown detail 2), not the
	 * native dblclick: the first click anchors the edit popover, which remounts
	 * the pill node, so the browser's dblclick lands on a replaced node and
	 * never reaches React. The press count survives remounts (the browser
	 * tracks it by time/position, not node identity). */
	onDoubleClick?: () => void;
	/** Right-click — the planner's book/invite context menu on open shifts. */
	onContextMenu?: (e: React.MouseEvent) => void;
	onRemove?: () => void;
	className?: string;
}

const variantClasses: Record<ShiftPillVariant, string> = {
	booked: "border-success/40 bg-success-muted text-green-950 hover:border-success",
	unassigned: "border-gray-400 bg-gray-300 text-gray-800 hover:border-gray-500",
	// Read-only MIRROR of a demand-row shift on the invited consultant's row —
	// white + dashed reads as transient (nothing is staffed yet); delete-only
	// via the ✕ (withdraws the invitation)
	invited: "cursor-default border-dashed border-gray-400 bg-white text-gray-800",
	absence: "border-danger/40 bg-danger-muted text-danger cursor-default",
};

/**
 * One shift inside a ShiftPlanner cell — times, optional break line, a remove
 * ✕ and corner badges with pending counts: filled blue = applications
 * (inbound, needs an answer), outlined = invitations (outbound, waiting).
 * Clicking the badges opens a card listing each pending consultant and every
 * shift of theirs in the job; hovering an entry outlines those shifts in the
 * grid, and the entry's button books the applicant / withdraws the
 * invitation. Green = booked, grey = unassigned ("Välj konsult" row), red =
 * absence (locked).
 */
const ShiftPill: React.FC<ShiftPillProps> = ({
	shift,
	variant,
	count = 1,
	onCountClick,
	narrow = false,
	highlighted = false,
	getPendingDetails,
	onHighlightShifts,
	cardOpen = false,
	onCardOpenChange,
	onAcceptEntry,
	onUninviteEntry,
	onClick,
	onDoubleClick,
	onContextMenu,
	onRemove,
	className,
}) => {
	const { t } = useTranslation();

	const appliedCount = shift.appliedConsultantNames?.length || 0;
	const invitedCount = shift.invitedConsultantNames?.length || 0;

	// Details are resolved only while the card is open — getPendingDetails
	// walks every shift of the job, far too much for plain grid renders
	const details = cardOpen && getPendingDetails ? getPendingDetails(shift) : [];

	return (
		<div
			role={variant !== "absence" && onClick ? "button" : undefined}
			tabIndex={variant !== "absence" && onClick ? 0 : undefined}
			onClick={variant === "absence" ? undefined : onClick}
			onMouseDown={
				onDoubleClick && variant !== "absence"
					? (e) => {
							// Shift = selection gesture, and the second press must not
							// arm the cell's drag/selection handling underneath
							if (e.detail === 2 && e.button === 0 && !e.shiftKey) {
								e.preventDefault();
								e.stopPropagation();
								onDoubleClick();
							}
					  }
					: undefined
			}
			onContextMenu={onContextMenu}
			onKeyDown={(e) => {
				if (variant !== "absence" && onClick && (e.key === "Enter" || e.key === " ")) {
					e.preventDefault();
					onClick();
				}
			}}
			className={cn(
				"relative flex h-full min-h-[52px] w-full items-center justify-between rounded border py-1 text-left transition-shadow duration-150",
				narrow ? "gap-0.5 pl-1.5 pr-0.5" : "gap-1.5 pl-2 pr-1",
				variantClasses[variant],
				// Even all-side drop shadow + outline (solid = applied, dashed =
				// invited) so the consultant's shifts pop without repainting or
				// moving the pill
				highlighted && "shadow-[0_0_10px_rgba(0,0,0,0.35)]",
				highlighted === "applied" && "outline outline-2 outline-action",
				highlighted === "invited" && "outline-dashed outline-2 outline-action",
				className
			)}
		>
			<div className="flex min-w-0 flex-col justify-center">
				{variant === "invited" && (
					<span className="text-[11px] font-medium leading-tight text-orange-600">
						{t("shiftPlanner.invited")}
					</span>
				)}
				<span
					className={cn(
						"whitespace-nowrap text-[13px] font-semibold leading-tight",
						narrow && "tracking-tighter"
					)}
				>
					{shift.startTime}
					<span className="mx-[1px]">-</span>
					{shift.endTime}
				</span>
				{variant === "absence" ? (
					<span className="text-xs leading-tight">{t("shiftPlanner.absence")}</span>
				) : (
					(count > 1 || !!shift.breakHours) && (
						// Second line, packed left: the parallel-count capsule and the
						// break share it so the pill never grows a third line
						<span className="mt-0.5 flex items-center gap-1">
							{count > 1 &&
								(onCountClick ? (
									<button
										type="button"
										aria-label={t("shiftPlanner.expandGroup") as string}
										title={t("shiftPlanner.expandGroup") as string}
										onClick={(e) => {
											// The capsule toggles the fan-out — never open the editor
											e.stopPropagation();
											onCountClick();
										}}
										className="whitespace-nowrap rounded-full bg-white px-1.5 text-[11px] font-bold leading-tight hover:ring-1 hover:ring-gray-400"
									>
										x {count}
									</button>
								) : (
									<span className="whitespace-nowrap rounded-full bg-white px-1.5 text-[11px] font-bold leading-tight">
										x {count}
									</span>
								))}
							{!!shift.breakHours && (
								<span className="whitespace-nowrap text-xs leading-tight">
									{t("shiftPlanner.breakShort", { hours: formatNumber(shift.breakHours, 0) })}
								</span>
							)}
						</span>
					)
				)}
			</div>
			{/* Own flex item so it stays vertically centered with or without a second line */}
			{onRemove && variant !== "absence" && (
				<button
					type="button"
					aria-label={t("shiftPlanner.removeShift") as string}
					onClick={(e) => {
						e.stopPropagation();
						onRemove();
					}}
					className="shrink-0 rounded p-0.5 opacity-70 hover:bg-black/10 hover:opacity-100"
				>
					<X className="h-3.5 w-3.5" />
				</button>
			)}
			{/* The mirror pill IS the invitation — its badge would just count
			    itself; badges and the pending card live on the demand-row
			    original */}
			{variant !== "invited" && (appliedCount > 0 || invitedCount > 0) && (
				<Popover
					open={cardOpen}
					onOpenChange={(open) => {
						onCardOpenChange?.(open);
						if (!open) onHighlightShifts?.([]);
					}}
				>
					<PopoverTrigger asChild>
						{/* One segmented capsule (blue = applied, white = invited) — two
						    full badges crowd the pill corner. Clicking opens the card,
						    never the shift editor. */}
						<button
							type="button"
							aria-label={t("shiftPlanner.pendingDetails") as string}
							onClick={(e) => e.stopPropagation()}
							onKeyDown={(e) => e.stopPropagation()}
							className="absolute -right-1 -top-1.5 flex items-stretch overflow-hidden rounded-full border border-gray-400 bg-white"
						>
							{appliedCount > 0 && (
								<span className="flex h-4 min-w-4 items-center justify-center bg-action px-1 text-[10px] font-bold leading-none text-white">
									{appliedCount}
								</span>
							)}
							{invitedCount > 0 && (
								<span className="flex h-4 min-w-4 items-center justify-center px-1 text-[10px] font-bold leading-none text-gray-700">
									{invitedCount}
								</span>
							)}
						</button>
					</PopoverTrigger>
					<PopoverContent
						align="start"
						sideOffset={6}
						onClick={(e) => e.stopPropagation()}
						className="w-auto min-w-[240px] max-w-[340px] p-1.5"
					>
						<div className="flex flex-col gap-0.5">
							{details.map((entry) => (
								<div
									key={`${entry.status}:${entry.name}`}
									onMouseEnter={() => onHighlightShifts?.(entry.shifts)}
									onMouseLeave={() => onHighlightShifts?.([])}
									className="rounded-md p-2 hover:bg-gray-100"
								>
									<div className="flex items-center justify-between gap-3">
										<span className="text-sm font-semibold">{entry.name}</span>
										<span
											className={cn(
												"rounded-full px-1.5 py-0.5 text-[11px] font-bold leading-none",
												entry.status === "applied"
													? "bg-action text-white"
													: "border border-gray-400 text-gray-700"
											)}
										>
											{t(`shiftPlanner.${entry.status}`)}
										</span>
									</div>
									{entry.shifts.length > 0 && (
										<div className="mt-1.5 flex flex-wrap gap-1">
											{entry.shifts.map((s) => (
												<span
													key={s.id}
													title={`${s.startTime} - ${s.endTime}`}
													className={cn(
														"rounded px-1 py-0.5 text-[11px] font-medium capitalize leading-none",
														s.status === "applied"
															? "bg-action/10 text-action"
															: "border border-gray-300 text-gray-600"
													)}
												>
													{dayjs(s.date).locale("sv").format("ddd D/M")}
												</span>
											))}
										</div>
									)}
									{entry.status === "applied" && onAcceptEntry && entry.consultantId && (
										<div className="mt-1.5 flex justify-end">
											<button
												type="button"
												onClick={() => {
													// Booking closes the card (like the consultant panel)
													onHighlightShifts?.([]);
													onCardOpenChange?.(false);
													onAcceptEntry(entry);
												}}
												// Same green as the consultant panel's "Boka"
												className="rounded border-0 bg-success px-2.5 py-1 text-xs font-semibold text-white hover:opacity-90"
											>
												{t("shiftPlanner.acceptApplication")}
											</button>
										</div>
									)}
									{entry.status === "invited" && onUninviteEntry && entry.consultantId && (
										<div className="mt-1.5 flex justify-end">
											<button
												type="button"
												onClick={() => {
													// The card stays open — several invitations can be
													// withdrawn in a row (the entry just disappears)
													onHighlightShifts?.([]);
													onUninviteEntry(entry);
												}}
												// Filled orange = the panel's "Önska" in its active state —
												// clicking withdraws the invitation there too
												className="rounded border border-solid border-orange-400 bg-orange-400 px-2.5 py-1 text-xs font-medium text-white hover:bg-orange-500"
											>
												{t("shiftPlanner.removeInvitation")}
											</button>
										</div>
									)}
								</div>
							))}
						</div>
					</PopoverContent>
				</Popover>
			)}
		</div>
	);
};

export default ShiftPill;
